import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import UserDropdown from "@/components/UserDropdown";
import {
  LayoutDashboard, Layout, Key, Settings, Users, CreditCard,
  LogOut, Code, BarChart3, ChevronRight, ChevronLeft,
  Headphones, Shield, Menu, X, UserPlus, Gift
} from "lucide-react";

const menuItems = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "layouts", label: "Layouts", icon: Layout },
  { id: "organisers", label: "Organisers", icon: Users },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "api-keys", label: "API Keys", icon: Key },
  { id: "integration", label: "Integration", icon: Code },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "referrals", label: "Referrals", icon: Gift },
  { id: "team", label: "Team", icon: UserPlus },
  { id: "support", label: "Support", icon: Headphones },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function DashboardShell({ children, pageTitle, activeItem = "layouts" }) {
  const navigate = useNavigate();
  const { user, logout, hasAdminAccess } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 1024);

  const allMenuItems = hasAdminAccess
    ? [...menuItems, { id: "admin", label: "Admin Panel", icon: Shield }]
    : menuItems;

  const handleMenuClick = (id) => {
    if (id === "admin") {
      // Single-tab navigation. Previously this called window.open() which
      // spawned a new tab on every click — confusing for super-admins who
      // toggle between user and admin views frequently. They can still use
      // ⌘-click / middle-click to open in a new tab if they want.
      navigate("/backoffice/dashboard");
    } else {
      navigate(`/dashboard?tab=${id}`);
    }
    if (window.innerWidth < 1024) setSidebarCollapsed(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2" onClick={() => navigate("/")}>
          <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">SP</span>
          </div>
          <span className="text-lg font-bold text-gray-900">SeatPilot</span>
        </div>
        <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-2 hover:bg-gray-100 rounded-lg">
          {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {!sidebarCollapsed && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarCollapsed(true)} />
      )}

      {/* Sidebar */}
      <aside className={`
        ${sidebarCollapsed ? "w-16 -translate-x-full md:translate-x-0" : "w-64 translate-x-0"}
        bg-white border-r border-gray-200 fixed h-full transition-all duration-300 z-50
        md:translate-x-0 flex flex-col
      `}>
        {/* Mobile sidebar header */}
        <div className={`md:hidden h-14 flex items-center justify-between px-4 border-b border-gray-200 ${sidebarCollapsed ? "hidden" : ""}`}>
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SP</span>
            </div>
            <span className="text-lg font-bold text-gray-900">SeatPilot</span>
          </div>
          <button onClick={() => setSidebarCollapsed(true)} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {/* Desktop branding */}
          <div className={`hidden md:flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"} mb-8`}>
            <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate("/")} title="Go to Homepage">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">SP</span>
              </div>
              {!sidebarCollapsed && <span className="text-xl font-bold text-gray-900">SeatPilot</span>}
            </div>
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1 hover:bg-gray-100 rounded-lg" title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

          <nav className="space-y-1">
            {allMenuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className={`w-full flex items-center ${sidebarCollapsed ? "justify-center" : ""} gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeItem === item.id
                    ? "bg-violet-50 text-violet-700"
                    : item.id === "admin"
                      ? "text-violet-600 hover:bg-violet-50 bg-violet-50/50"
                      : "text-gray-600 hover:bg-gray-50"
                }`}
                title={sidebarCollapsed ? item.label : ""}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            ))}
          </nav>
        </div>

        {/* User info + sign out */}
        <div className={`p-4 border-t border-gray-200 ${sidebarCollapsed ? "px-2" : ""}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                <span className="text-violet-600 font-medium">
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || "User"}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            className={`flex items-center ${sidebarCollapsed ? "justify-center" : ""} gap-2 text-sm text-gray-600 hover:text-gray-900 w-full px-3 py-2 rounded-lg hover:bg-gray-50`}
            title={sidebarCollapsed ? "Sign out" : ""}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && "Sign out"}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 pt-14 md:pt-0 ${sidebarCollapsed ? "md:ml-16" : "md:ml-64"}`}>
        {/* Top Header Bar — sticky only on desktop to avoid 112px gap on mobile
            (parent has pt-14 + sticky top-14 stacks the top offset twice). */}
        <div className="md:sticky md:top-0 z-30 bg-white border-b border-gray-200 px-4 md:px-8 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-base md:text-lg font-semibold text-gray-900">{pageTitle || "Layout Info"}</h1>
            <div className="flex items-center gap-2 md:gap-4">
              <NotificationBell
                isAdmin={false}
                context="user"
                onOpenTicket={() => navigate("/dashboard?tab=support")}
                onGoToSupport={() => navigate("/dashboard?tab=support")}
              />
              <UserDropdown onNavigate={(tab) => navigate(`/dashboard?tab=${tab}`)} />
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
